/** Signed distance to a zone shape: negative inside, positive outside, metres. */
import type { ProfilePoint, Shape } from './shapes';
import type { Box, Vec3 } from '../model/types';

/** Signed distance from a 2D point to a closed polygon (the profile closed along the axis). */
function polygonSdf(px: number, pz: number, poly: ProfilePoint[]): number {
  let d = Infinity;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [ax, az] = poly[j]!;
    const [bx, bz] = poly[i]!;
    const ex = bx - ax;
    const ez = bz - az;
    const len2 = ex * ex + ez * ez;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * ex + (pz - az) * ez) / len2)) : 0;
    const dx = px - (ax + ex * t);
    const dz = pz - (az + ez * t);
    d = Math.min(d, dx * dx + dz * dz);
    if (az > pz !== bz > pz && px < ((bx - ax) * (pz - az)) / (bz - az) + ax) inside = !inside;
  }
  return inside ? -Math.sqrt(d) : Math.sqrt(d);
}

/** The full cross-section through the axis: the profile and its mirror, so the axis is inside. */
const sections = new WeakMap<ProfilePoint[], ProfilePoint[]>();
function crossSection(profile: ProfilePoint[]): ProfilePoint[] {
  let poly = sections.get(profile);
  if (!poly) {
    poly = [...profile, ...profile.slice().reverse().map(([r, z]): ProfilePoint => [-r, z])];
    sections.set(profile, poly);
  }
  return poly;
}

export function boxSdf(box: Box, p: Vec3): number {
  const [cx, cy, cz] = box.center;
  const c = Math.cos(-box.yawRad);
  const s = Math.sin(-box.yawRad);
  const lx = c * (p[0] - cx) - s * (p[1] - cy);
  const ly = s * (p[0] - cx) + c * (p[1] - cy);
  const lz = p[2] - cz;
  const qx = Math.abs(lx) - box.size[0] / 2;
  const qy = Math.abs(ly) - box.size[1] / 2;
  const qz = Math.abs(lz) - box.size[2] / 2;
  const outside = Math.hypot(Math.max(qx, 0), Math.max(qy, 0), Math.max(qz, 0));
  return outside + Math.min(Math.max(qx, qy, qz), 0);
}

export function shapeSdf(shape: Shape, p: Vec3): number {
  if (shape.kind === 'box') return boxSdf(shape.box, p);
  const rho = Math.hypot(p[0] - shape.axis[0], p[1] - shape.axis[1]);
  return polygonSdf(rho, p[2], crossSection(shape.profile));
}

// ---------------------------------------------------------------- fast queries

export interface Aabb { min: Vec3; max: Vec3 }

interface RadiusTable {
  /** Profile heights, non-decreasing from the bottom of the shape to the top. */
  z: number[];
  r: number[];
  zMin: number;
  zMax: number;
  rMax: number;
}

const tables = new WeakMap<ProfilePoint[], RadiusTable | null>();

/**
 * Every profile Extent builds gives one radius per height (it rises monotonically from the bottom of
 * the axis to the top). That makes "is this point inside?" a binary search. Returns null for a profile
 * that does not have this property, so callers fall back to the exact polygon test.
 */
function radiusTable(profile: ProfilePoint[]): RadiusTable | null {
  if (tables.has(profile)) return tables.get(profile)!;
  let t: RadiusTable | null = { z: profile.map((p) => p[1]), r: profile.map((p) => p[0]), zMin: profile[0]![1], zMax: profile[profile.length - 1]![1], rMax: Math.max(...profile.map((p) => p[0])) };
  for (let i = 1; i < t.z.length; i++) if (t.z[i]! < t.z[i - 1]! - 1e-12) { t = null; break; }
  tables.set(profile, t);
  return t;
}

/** Largest radius of the shape at height z (the profile is a function of z, caps take their widest point). */
function radiusAt(t: RadiusTable, z: number): number {
  if (z < t.zMin || z > t.zMax) return -1;
  let lo = 0, hi = t.z.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (t.z[mid]! <= z) lo = mid; else hi = mid;
  }
  // horizontal runs at exactly this height: take the widest point on them
  let best = -1;
  for (let i = lo; i >= 0 && t.z[i] === z; i--) best = Math.max(best, t.r[i]!);
  for (let i = hi; i < t.z.length && t.z[i] === z; i++) best = Math.max(best, t.r[i]!);
  const z0 = t.z[lo]!, z1 = t.z[hi]!;
  const interp = z1 > z0 ? t.r[lo]! + ((t.r[hi]! - t.r[lo]!) * (z - z0)) / (z1 - z0) : Math.max(t.r[lo]!, t.r[hi]!);
  return Math.max(best, interp);
}

/** True when p is inside (or on) the shape. Much faster than the signed distance for revolved shapes. */
export function insideShape(shape: Shape, p: Vec3): boolean {
  if (shape.kind === 'box') return boxSdf(shape.box, p) <= 0;
  const t = radiusTable(shape.profile);
  if (!t) return shapeSdf(shape, p) <= 0;
  const rho = Math.hypot(p[0] - shape.axis[0], p[1] - shape.axis[1]);
  if (rho > t.rMax) return false;
  return rho <= radiusAt(t, p[2]) + 1e-9;
}

export function boxAabb(box: Box): Aabb {
  const c = Math.abs(Math.cos(box.yawRad)), s = Math.abs(Math.sin(box.yawRad));
  const hx = (c * box.size[0] + s * box.size[1]) / 2, hy = (s * box.size[0] + c * box.size[1]) / 2, hz = box.size[2] / 2;
  const [x, y, z] = box.center;
  return { min: [x - hx, y - hy, z - hz], max: [x + hx, y + hy, z + hz] };
}

const aabbs = new WeakMap<Shape, Aabb>();

export function shapeAabb(shape: Shape): Aabb {
  const hit = aabbs.get(shape);
  if (hit) return hit;
  const a = computeAabb(shape);
  aabbs.set(shape, a);
  return a;
}

function computeAabb(shape: Shape): Aabb {
  if (shape.kind === 'box') return boxAabb(shape.box);
  const rMax = Math.max(...shape.profile.map((p) => p[0]));
  const zs = shape.profile.map((p) => p[1]);
  return { min: [shape.axis[0] - rMax, shape.axis[1] - rMax, Math.min(...zs)], max: [shape.axis[0] + rMax, shape.axis[1] + rMax, Math.max(...zs)] };
}

export function aabbOverlap(a: Aabb, b: Aabb, margin = 0): boolean {
  for (let i = 0; i < 3; i++) if (a.max[i]! + margin < b.min[i]! || b.max[i]! + margin < a.min[i]!) return false;
  return true;
}
